import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetActiveRidersQuery } from "../../Pages/Dashboard/DashboardApi";
import { updateAvailableRiders } from "../slices/locationSlice";
import { isDisplayableActiveRider } from "../riders";

export const useScreenWidth = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return screenWidth;
};

export const useUpdateDriverStatus = () => {
  const dispatch = useDispatch();
  const { currentOrders, riderLocation } = useSelector(
    (state) => state.location
  );
  const [drivers, setDrivers] = useState([]);
  //Once launched, fetch all active riders
  const [getRider, { isLoading }] = useLazyGetActiveRidersQuery();

  const handleDriverStatus = useCallback(async () => {
    const { data: riders } = await getRider();
    // Assumption — dispatchStatus values that mean "actively assigned"
    // should be verified against the real backend enum.
    const ACTIVELY_ASSIGNED_DISPATCH_STATUSES = ["assigned", "accepted"];
    const newRiderOrders = currentOrders?.filter(
      (e) =>
        ACTIVELY_ASSIGNED_DISPATCH_STATUSES.includes(e?.dispatchStatus) &&
        e?.status !== "delivered" &&
        e?.status !== "cancelled"
    );

    const riderOrderCount = newRiderOrders?.reduce((acc, order) => {
      acc[order.riderId] = (acc[order.riderId] || 0) + 1;
      return acc;
    }, {});
    const activeRiders = (riders?.data || []).filter(isDisplayableActiveRider);
    const activeRiderById = new Map(
      activeRiders.map((rider) => [rider.riderId, rider]),
    );

    const updatedDrivers = riderLocation
      ?.filter((driver) => activeRiderById.has(driver.riderId))
      ?.map((driver) => {
        const matchedRider = activeRiderById.get(driver.riderId);
        const isActive = newRiderOrders?.some(
          (order) => order.riderId === driver.riderId
        );
        const orderCount = riderOrderCount[driver.riderId] || 0;
        return {
          value: driver.riderId,
          label: matchedRider
            ? `${matchedRider.firstName} ${matchedRider.lastName}`
            : "Unknown Rider",
          active: isActive ? true : false,
          location: driver?.location,
          available: orderCount < 2,
        };
      })
      .filter((driver) => driver.available);

    dispatch(updateAvailableRiders(updatedDrivers));
    setDrivers(updatedDrivers);
  }, [currentOrders, riderLocation]);

  const memoizedDrivers = useMemo(() => drivers, [drivers]);

  useEffect(() => {
    if (currentOrders?.length || riderLocation?.length) {
      handleDriverStatus();
    }
  }, [currentOrders, riderLocation, handleDriverStatus]);

  return { drivers: memoizedDrivers, isLoading };
};

export const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
